import { useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { VendorDocument } from '@cmms/api-client'
import { DocumentUploader } from './DocumentUploader'
import { toast } from '../store/snackbarStore'

interface Props {
  queryKey: string[]
  listFn: () => Promise<VendorDocument[]>
  uploadFn: (file: File, onProgress: (pct: number) => void) => Promise<any>
  viewFn: (doc: VendorDocument) => Promise<{ data: Blob }>
  deleteFn: (docId: string) => Promise<any>
  canEdit: boolean
  maxFiles?: number
  title?: string
}

export function AttachmentSection({ queryKey, listFn, uploadFn, viewFn, deleteFn, canEdit, maxFiles = 10, title = 'Attachments' }: Props) {
  const qc = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)

  const docsQ = useQuery({ queryKey, queryFn: listFn })
  const docs: VendorDocument[] = Array.isArray(docsQ.data) ? docsQ.data : []

  async function handleUpload(file: File) {
    setUploading(true); setProgress(0)
    try {
      await uploadFn(file, (p) => setProgress(p))
      qc.invalidateQueries({ queryKey })
      toast.success('File uploaded')
    } catch { toast.error('Upload failed') }
    finally { setUploading(false); setProgress(null) }
  }

  async function handleDelete(docId: string) {
    try {
      await deleteFn(docId)
      qc.invalidateQueries({ queryKey })
      toast.success('File removed')
    } catch { toast.error('Failed to remove') }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {title} {docs.length > 0 && `(${docs.length})`}
        </Typography>
      </Stack>
      <DocumentUploader
        documents={docs}
        maxFiles={maxFiles}
        uploading={uploading}
        uploadProgress={progress}
        onUpload={handleUpload}
        onDelete={handleDelete}
        onView={(doc) => viewFn(doc)}
        canEdit={canEdit}
      />
    </Box>
  )
}
